import imageCompression from 'browser-image-compression';
import { deleteUploadedImage, uploadBlogMedia, uploadDocument, uploadDocumentByPut, uploadPartnerLogo, uploadProductIcon } from '../api/requests';
import { DELETE_IMAGE_ENDPOINTS, POLICY_DOCUMENT_TYPES, UPLOADABLE_DOCUMENT_TYPES, UPLOAD_IMAGE_ENDPOINTS } from '../resources/enums';
import { blobToFile } from '../utils/miscFunctions';
import { IPolicyDocument } from '../resources/interfaces';
import PaymentAgreementIcon from './imgs/contract (1).png';
import ContractSummary from './imgs/contract.png';
import PolicyStickerIcon from './imgs/shield.png';
import RenewalNoticeIcon from './imgs/calendar.png';
import DriverLicenseIcon from './imgs/id-card.png';
import InsuranceCertificateIcon from './imgs/file-protection.png';
import RoadWorthyIcon from './imgs/car-insurance.png';
import IdCard from './imgs/OWNER_ID.png';
import OWNER_PHOTO from './imgs/OWNER_PHOTO.png';
import { GrDocumentTime } from 'react-icons/gr';

const compressOptions = {
  maxSizeMB: 5,
  maxWidthOrHeight: 1920,
  useWebWorker: true
};

function useImage() {
  const uploadImage = (image: File, url: string, requestMethod = 'post', fileName = 'file') => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image, fileName);

      if (requestMethod === 'post') {
        uploadDocument(`${url}`, imageData)
          .then((response: any) => {
            resolve(response);
          })
          .catch((error: Error) => {
            reject(error);
          });
      }

      if (requestMethod === 'put') {
        uploadDocumentByPut(`${url}`, imageData)
          .then((response: any) => {
            resolve(response);
          })
          .catch((error: Error) => {
            reject(error);
          });
      }
    });
  };

  const uploadStaffImage = (
    image: File,
    documentType: UPLOADABLE_DOCUMENT_TYPES,
    url: UPLOAD_IMAGE_ENDPOINTS
  ) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(image);

      uploadDocument(`${url}?documentType=${documentType}`, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadVehicleImage = (
    image: File | '',
    documentType: UPLOADABLE_DOCUMENT_TYPES,
    url: UPLOAD_IMAGE_ENDPOINTS,
    vehicleId: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      let imageData = new FormData();
      if (image) {
        imageData = getImageFormData(image);
      }

      imageData.append('documentType', documentType);
      imageData.append('vehicleId', vehicleId);

      uploadDocument(`${url}`, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadPolicyOwnerDocument = (
    file: File | '',
    documentType: UPLOADABLE_DOCUMENT_TYPES,
    url: UPLOAD_IMAGE_ENDPOINTS,
    customerId: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      let fileData = new FormData();
      if (file) {
        fileData = getImageFormData(file);
      }

      // fileData.append('documentType', documentType);
      // fileData.append('customerId', customerId);

      uploadDocument(`${url}?documentType=${documentType}&id=${customerId}&allowMultiple=true`, fileData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadPolicyDocument = (
    file: File | '',
    documentType: UPLOADABLE_DOCUMENT_TYPES,
    url: UPLOAD_IMAGE_ENDPOINTS,
    policyNumber: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      let fileData = new FormData();
      if (file) {
        fileData = getImageFormData(file);
      }

      fileData.append('documentType', documentType);
      fileData.append('policyNumber', policyNumber);

      uploadDocument(`${url}?documentType=${documentType}&id=${policyNumber}&allowMultiple=true`, fileData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const getImageFormData = (image: File, imageFieldName = 'file', compress = false) => {
    const data = new FormData();

    if (compress) {
      const compressedFile = getCompressedImage(image);
      data.append(imageFieldName, blobToFile(compressedFile, image));
    } else {
      data.append(imageFieldName, image);
    }

    return data;
  };

  const getCompressedImage = async (image: File) => {
    const compressedFile = await imageCompression(image, compressOptions);

    return compressedFile;
  };

  const removeImage = (url: DELETE_IMAGE_ENDPOINTS) => {
    return new Promise<void>((resolve, reject) => {
      deleteUploadedImage(`${url}`)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const productIconUpload = (productId: string, file: File) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(file);
      uploadProductIcon(productId, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const partnerLogoUpload = (partnerId: string, file: File) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(file);
      uploadPartnerLogo(partnerId, imageData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadBlogPhoto = (blogId: string, file: File, isBanner: boolean) => {
    return new Promise<void>((resolve, reject) => {
      const imageData = getImageFormData(file);
      uploadBlogMedia(blogId, imageData, isBanner)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const uploadClaimDocument = (
    file: File | '',
    documentType: UPLOADABLE_DOCUMENT_TYPES,
    url: UPLOAD_IMAGE_ENDPOINTS,
    claimNumber: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      let fileData = new FormData();
      if (file) {
        fileData = getImageFormData(file);
      }

      fileData.append('documentType', documentType);
      fileData.append('claimNumber', claimNumber);

      uploadDocument(`${url}?documentType=${documentType}&id=${claimNumber}`, fileData)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };
  const previewDocument = (document: IPolicyDocument) => { window.open(document.docUrl, '_blank'); };

  return {
    getCompressedImage,
    getImageFormData,
    uploadImage,
    uploadVehicleImage,
    uploadPolicyDocument,
    uploadPolicyOwnerDocument,
    uploadStaffImage,
    removeImage,
    productIconUpload,
    partnerLogoUpload,
    uploadBlogPhoto,
    previewDocument,
    uploadClaimDocument
  };
}

export default useImage;
